import React, { ComponentProps, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { FilesFilterPopup } from '@components/Web/PopupFilesFilter.web';
import { useOutsideClick } from '@hooks/useOutsideClick.web';

import { getScaledSize, SCALED_DIMENSIONS } from '../../../themes/theme';

type PositionTuple = ['left' | 'right', 'top' | 'bottom'];

type Position = PositionTuple | 'top' | 'bottom' | 'left' | 'right';

type Props = Omit<ComponentProps<typeof TouchableOpacity>, 'onPress'> & {
  title: string;
  onDismiss?: any;
  isMyUploads?: boolean;
  setIsMyUploads?: (value: boolean) => void;
  filesFilter?: string[];
  setFilesFilter: (value: string[]) => void;
  boxProps: Partial<{
    position: Position;
    offset: [number, number] | number;
  }>;
  maxHeight?: number;
};

export const PopupFilesFilterButtonWeb = ({
  onDismiss,
  isMyUploads = false,
  setIsMyUploads,
  filesFilter = [],
  setFilesFilter,
  boxProps: { position, offset, ...popupPropsRest },
  title,
  maxHeight,
}: Props) => {
  const { t } = useTranslation('shared');
  const [visible, setVisible] = useState(false);
  const [mouseOver, setMouseOver] = useState(false);
  const badgeNumber = (isMyUploads ? 1 : 0) + filesFilter.length;

  const togglePopup = () => setVisible((v) => !v);

  const ref2 = useRef<HTMLDivElement>();

  const isEventPointInModal = (event: MouseEvent) => {
    const rect = ref2?.current?.getBoundingClientRect();
    return (
      rect &&
      event.x > rect.x &&
      event.x < rect.x + rect.width &&
      event.y > rect.y &&
      event.y < rect.y + rect.height
    );
  };
  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (!isEventPointInModal(event)) {
      setVisible(false);
    }
  }, []);

  const ref = useOutsideClick<HTMLDivElement>(handleOutsideClick);

  // Store the natural dimensions of the popup container
  const [layout, setLayout] = useState<
    { width: number; height: number } | undefined
  >();

  const adjustBounds = (
    position: Props['boxProps']['position'] = 'bottom',
    offset: Props['boxProps']['offset'] = 0,
    client: DOMRect,
    popupLayout: { width: number; height: number }
  ) => {
    const [offsetLeftRight, offsetTopBottom] =
      typeof offset === 'number' ? [offset, offset] : offset;

    const normalizedPosition = (typeof position !== 'string' && position) ||
      ((position === 'left' || position === 'right') && [position, 'bottom']) ||
      ((position === 'top' || position === 'bottom') && ['left', position]) || [
        'left',
        'bottom',
      ];

    const [leftRight, topBottom] = normalizedPosition;
    const bounds = {
      top:
        topBottom === 'top'
          ? client.top - popupLayout.height - offsetTopBottom
          : client.top + client.height + offsetTopBottom,
      left:
        leftRight === 'left'
          ? client.left + client.width - popupLayout.width - offsetLeftRight
          : client.left + offsetLeftRight,
    };

    return bounds;
  };

  const adjustedBounds =
    ref.current &&
    layout &&
    adjustBounds(position, offset, ref.current.getBoundingClientRect(), layout);

  const onModalDismiss = () => {
    onDismiss?.();
    setVisible(false);
  };

  const backgroundColor =
    mouseOver || badgeNumber > 0 || visible ? 'grey01' : 'white';

  const clearAll = () => {
    setIsMyUploads?.(false);
    setFilesFilter([]);
  };

  return (
    <Box ref={ref}>
      <TouchableOpacity
        accessibilityLabel='Files Filter'
        onPress={() => {
          togglePopup();
        }}>
        <Box
          height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
          flexDirection='row'
          px='m'
          py='xs'
          borderRadius='xs'
          alignItems='center'
          justifyContent='center'
          onMouseEnter={() => {
            setMouseOver(true);
          }}
          onMouseLeave={() => {
            setMouseOver(false);
          }}
          backgroundColor={backgroundColor}>
          <Icon name='AlignCenterDesc' variant='s' color='black' />
          <Text variant='webSmall' color='black' ml='xs'>
            {title}
          </Text>
          {badgeNumber > 0 ? (
            <Box
              marginLeft='xs'
              minWidth={20}
              height={20}
              px='xxs'
              alignItems='center'
              justifyContent='center'
              backgroundColor='greenSecondary'
              borderRadius='s'>
              <Text variant='webBodyHintSubline' color='white'>
                {badgeNumber}
              </Text>
            </Box>
          ) : (
            <></>
          )}
          {badgeNumber > 0 ? (
            <TouchableOpacity
              accessibilityRole='button'
              accessibilityLabel={t('clearAll')}
              onPress={() => {
                clearAll();
              }}>
              <Box
                width={16}
                height={16}
                marginLeft='xs'
                alignItems='center'
                justifyContent='center'>
                <Icon variant='xxxs' name='Close' color='black' />
              </Box>
            </TouchableOpacity>
          ) : (
            <></>
          )}
        </Box>
      </TouchableOpacity>

      {!layout && (
        <Box
          position='absolute'
          left='-200vw'
          visible={!layout}
          onLayout={({ nativeEvent: { layout } }) => {
            setLayout((curr) => curr ?? layout);
          }}>
          {setFilesFilter && (
            <FilesFilterPopup
              isMyUploads={isMyUploads}
              setIsMyUploads={setIsMyUploads}
              filesFilter={filesFilter}
              setFilesFilter={setFilesFilter}
              {...popupPropsRest}
              maxHeight={maxHeight}
            />
          )}
        </Box>
      )}
      <Modal
        visible={visible}
        transparent
        onDismiss={() => onModalDismiss()}
        onRequestClose={() => onModalDismiss()}>
        <Box ref={ref2} style={{ ...adjustedBounds }} width={layout?.width}>
          <Box flexShrink={1}>
            {setFilesFilter && (
              <FilesFilterPopup
                {...popupPropsRest}
                isMyUploads={isMyUploads}
                setIsMyUploads={setIsMyUploads}
                filesFilter={filesFilter}
                setFilesFilter={setFilesFilter}
                maxHeight={maxHeight}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
